"use client"

import * as React from "react"
import {
  User,
  Heart,
  Calendar,
  Pill,
  FileText,
  Settings,
  Phone,
  Mail,
  MapPin,
  Shield,
  Clock,
  Activity,
  Maximize,
  Minimize,
  Save,
  ChevronDown,
  ClipboardList,
  History,
} from "lucide-react"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./breadcrumb"
import { Button } from "./button"
import { Badge } from "./badge"
import { MedicationCard } from "./medication-card"
import { VisitCard } from "./visit-card"
import { VisitClinicalNote, type ClinicalNoteData } from "./visit-clinical-note"
import { MedicationAdjustmentForm, type MedicationAdjustmentData } from "./medication-adjustment-form"
import { TrackingChart, type MedicationOption } from "./tracking-chart"
import { EligibilitySection } from "./eligibility-section"
import { PrescriptionHistory } from "./prescription-history"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Label } from "./label"
import { Input } from "./input"
import { Textarea } from "./textarea"
import { Checkbox } from "./checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./dialog"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./tabs"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "./sidebar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { 
  supabase,
  authService,
  medicationTrackingService,
  ClinicalNotesService,
  AddendumService,
  FaxingService,
  ProviderMedicationService
} from "@joinomu/shared"
import { showToast } from "./toast"

export interface PatientMedicationPreference {
  id: string
  medication_id?: string
  medication_name: string
  preferred_dosage: string
  frequency?: string
  status: 'pending' | 'approved' | 'denied' | 'discontinued'
  requested_date: string
  notes?: string
  providerNotes?: string
  faxed?: string | null
  next_prescription_due?: string | null
  supply_days?: number
  other_dosage?: string
  stripe_subscription_id?: string | null
  // Joined medication data from the medications table
  medications?: {
    id: string
    name: string
    description?: string
    category?: string
  }
}

export interface PatientVisit {
  id: string
  appointment_date: string
  start_time: string
  appointment_type: string
  treatment_type: string
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show' | 'rescheduled'
  provider_notes?: string
  patient_notes?: string
  addendums?: PatientVisitAddendum[]
  prescription_filled?: boolean
}

export interface PatientVisitAddendum {
  id: string
  visit_id: string
  provider_id: string
  content: string
  created_at: string
}

export interface VisitMedicationAdjustment {
  id: string
  clinical_note_id: string
  appointment_id: string
  preference_id: string
  previous_dosage?: string
  previous_frequency?: string
  previous_status?: string
  previous_provider_notes?: string
  new_dosage?: string
  new_frequency?: string
  new_status?: string
  new_provider_notes?: string
  adjustment_reason?: string
  provider_notes?: string
  created_at: string
  performed_by: string
  new_supply_days?: number
  // Join with patient_medication_preferences for medication name
  medication_name?: string
}

export interface ProviderPatientData {
  id: string
  profile_id: string
  name: string
  email: string
  dateOfBirth?: string
  gender?: string
  patientId?: string
  phone?: string
  address?: string
  treatmentType?: string
  assignedDate?: string
  isPrimary?: boolean
  medicationPreferences?: PatientMedicationPreference[]
  visits?: PatientVisit[]
  // Eligibility/onboarding fields
  date_of_birth?: string
  selected_state?: string
  height_feet?: number
  height_inches?: number
  weight?: number
  bmi?: number
  activity_level?: string
  weight_loss_goals?: string
  motivations?: string | string[]
  treatment_preferences?: string | string[]
  // Medical history fields
  medication_preference?: string
  diagnosed_conditions?: string | string[]
  chronic_diseases?: string | string[]
  family_medical_history?: string | string[]
  family_health?: string | string[]
  allergies?: string | string[]
  medication_history?: string | string[]
  procedures?: string | string[]
  supplements?: string | string[]
  // Mental health fields
  eating_disorders?: string | string[]
  mental_health?: string | string[]
  self_harm_screening?: string | string[]
  // Substance use fields
  drinking?: string
  drugs?: string | string[]
  smoking?: string
  // Health metrics fields
  heart_rate?: string
  gastrointestinal?: string | string[]
  // Side effects fields
  side_effects?: string | string[]
  side_effect_guidance?: string | string[]
  gastrointestinal_dosing?: string
  energy_dosing?: string
  muscle_loss_dosing?: string
  // Program adherence fields
  challenges?: string | string[]
  challenges_elaborate?: string
  program_adherence?: string | string[]
  program_consistency?: string | string[]
  additional_info?: string
}

interface ProviderPatientInformationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patient: ProviderPatientData | null
  providerId?: string
  initialTab?: string
  initialVisitId?: string
  initialMedicationId?: string
  onMedicationUpdate?: (medicationId: string, updates: {
    status?: string
    dosage?: string
    frequency?: string
    providerNotes?: string
    faxed?: boolean
    supplyDays?: number
      medicationId?: string // Add support for changing the actual medication
  }) => Promise<void>
}

const patientNavData = {
  nav: [
    { name: "Patient Information", icon: User },
    { name: "Visits", icon: Calendar },
    { name: "History", icon: History },
    { name: "Tracking", icon: Activity },
    { name: "Eligibility", icon: ClipboardList },
  ],
}

export function ProviderPatientInformationDialog({
  open,
  onOpenChange,
  patient,
  providerId,
  initialTab,
  initialVisitId,
  initialMedicationId,
  onMedicationUpdate
}: ProviderPatientInformationDialogProps) {
  // Remove early return to fix React error #310 - hooks must always run in same order

  const [isFullscreen, setIsFullscreen] = React.useState(true)
  const [activeTab, setActiveTab] = React.useState(initialTab || "Patient Information")
  const [selectedVisit, setSelectedVisit] = React.useState<PatientVisit | null>(null)
  const [clinicalNoteData, setClinicalNoteData] = React.useState<ClinicalNoteData | null>(null)
  const [pendingMedicationChanges, setPendingMedicationChanges] = React.useState<MedicationAdjustmentData | null>(null)
  const [visitMedicationAdjustments, setVisitMedicationAdjustments] = React.useState<VisitMedicationAdjustment[]>([])
  const [isSaving, setIsSaving] = React.useState(false)
  const [visitsTab, setVisitsTab] = React.useState<'upcoming' | 'previous'>('upcoming')
  const [isHandlingClinicalNoteSave, setIsHandlingClinicalNoteSave] = React.useState(false)

  // Function to fetch visit medication adjustments
  const fetchVisitMedicationAdjustments = React.useCallback(async (visitId: string) => {
    try {
      console.log('🔍 Fetching visit medication adjustments for visit:', visitId)
      
      // Query the visit_medication_adjustments table with join to get medication names
      const { data: adjustments, error } = await supabase
        .from('visit_medication_adjustments')
        .select(`
          *,
          patient_medication_preferences!inner(medications(name))
        `)
        .eq('appointment_id', visitId)
        .order('created_at', { ascending: true })
      
      if (error) {
        console.error('Error fetching visit medication adjustments:', error)
        return
      }
      
      console.log('📋 Found visit medication adjustments:', adjustments)
      
      // Transform the data to include medication names
      const transformedAdjustments: VisitMedicationAdjustment[] = (adjustments || []).map(adj => ({
        id: adj.id,
        clinical_note_id: adj.clinical_note_id,
        appointment_id: adj.appointment_id,
        preference_id: adj.preference_id,
        previous_dosage: adj.previous_dosage,
        previous_frequency: adj.previous_frequency,
        previous_status: adj.previous_status,
        previous_provider_notes: adj.previous_provider_notes,
        new_dosage: adj.new_dosage,
        new_frequency: adj.new_frequency,
        new_status: adj.new_status,
        new_provider_notes: adj.new_provider_notes,
        adjustment_reason: adj.adjustment_reason,
        provider_notes: adj.provider_notes,
        created_at: adj.created_at,
        performed_by: adj.performed_by,
        new_supply_days: adj.new_supply_days,
        medication_name: adj.patient_medication_preferences?.medications?.name
      }))
      
      setVisitMedicationAdjustments(transformedAdjustments)
    } catch (error) {
      console.error('Error fetching visit medication adjustments:', error)
      setVisitMedicationAdjustments([])
    }
  }, [])

  // Create a wrapper for onMedicationUpdate that suppresses toasts during clinical note save
  const medicationUpdateWithToastControl = React.useCallback(async (medicationId: string, updates: any) => {
    if (!onMedicationUpdate) return
    
    if (isHandlingClinicalNoteSave) {
      // During clinical note save, we need to prevent the external function from showing its own toasts
      // We'll need to call the underlying service directly to bypass toast logic
      console.log('🔇 Suppressing medication update toasts during clinical note save')
      
      try {
        // Use the auth service directly to bypass the parent's toast logic
        
        // Get provider data for the service call
        const { data: providerData } = await supabase
          .from('providers')
          .select('id')
          .eq('profile_id', providerId)
          .single()
        
        if (providerData?.id) {
          await authService.updateMedicationPreference(medicationId, updates, providerData.id)
        }
      } catch (error) {
        console.error('Error in toast-suppressed medication update:', error)
        // Don't show toast during clinical note save - parent handles all toasts
      }
    } else {
      // Normal operation - let the parent function handle its own toasts
      await onMedicationUpdate(medicationId, updates)
    }
  }, [onMedicationUpdate, isHandlingClinicalNoteSave, providerId])
  const [newAddendum, setNewAddendum] = React.useState('')
  const [isAddingAddendum, setIsAddingAddendum] = React.useState(false)
  const [existingClinicalNote, setExistingClinicalNote] = React.useState<any>(null)
  const [loadingClinicalNote, setLoadingClinicalNote] = React.useState(false)
  const [healthMetricsData, setHealthMetricsData] = React.useState<any[]>([])
  const [medicationTrackingData, setMedicationTrackingData] = React.useState<any[]>([])
  const [currentMetricType, setCurrentMetricType] = React.useState<string>('weight')
  const [selectedMedicationsForTracking, setSelectedMedicationsForTracking] = React.useState<string[]>([])
  
  // Available medications for medication selector in visits section
  const [availableMedications, setAvailableMedications] = React.useState<Array<{id: string, name: string, brand_name?: string, category: string, available_dosages?: string[], medication_dosages?: Array<{id: string, strength: string}>}>>([])
  const [loadingAvailableMedications, setLoadingAvailableMedications] = React.useState(false)
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

  // Shipping address state
  const [shippingAddress, setShippingAddress] = React.useState<{
    street_line_1: string
    street_line_2?: string
    city: string
    state: string
    postal_code: string
    country: string
  } | null>(null)
  const [loadingAddress, setLoadingAddress] = React.useState(false)
  
  // Update activeTab when initialTab changes
  React.useEffect(() => {
    console.log('🔍 DIALOG: Initial tab effect triggered. initialTab:', initialTab)
    if (initialTab) {
      console.log('🔍 DIALOG: Setting active tab to:', initialTab)
      setActiveTab(initialTab)
    } else {
      // Reset to default tab when initialTab is undefined (patient table click)
      console.log('🔍 DIALOG: No initial tab provided, defaulting to Patient Information')
      setActiveTab("Patient Information")
      // Also clear any selected visit state to reset breadcrumb
      setSelectedVisit(null)
      setClinicalNoteData(null)
      setExistingClinicalNote(null)
      setPendingMedicationChanges(null)
      setNewAddendum('')
      setIsAddingAddendum(false)
    }
  }, [initialTab])

  // Auto-select visit when initialVisitId is provided
  React.useEffect(() => {
    console.log('🔍 DIALOG: Auto-select visit effect triggered')
    console.log('🔍 DIALOG: initialVisitId:', initialVisitId)
    console.log('🔍 DIALOG: patient?.visits?.length:', patient?.visits?.length)
    console.log('🔍 DIALOG: activeTab:', activeTab)
    
    if (initialVisitId && patient?.visits && patient.visits.length > 0) {
      const visit = patient.visits.find(v => v.id === initialVisitId)
      if (visit) {
        console.log('🔍 DIALOG: Auto-selecting visit:', visit.id, 'for patient:', patient.name)
        handleVisitSelect(visit)
      } else {
        console.warn('⚠️ DIALOG: Visit with ID', initialVisitId, 'not found in patient visits:', patient.visits.map(v => v.id))
      }
    } else {
      console.log('🔍 DIALOG: Not auto-selecting visit - missing conditions')
    }
  }, [initialVisitId, patient?.visits, patient?.name, activeTab])

  // Auto-select medication when initialMedicationId is provided
  React.useEffect(() => {
    if (initialMedicationId && patient?.medicationPreferences) {
      const medication = patient.medicationPreferences.find(m => m.id === initialMedicationId)
      if (medication) {
        console.log('🎯 AUTO-SELECTING MEDICATION FROM APPROVAL:', {
          medicationId: medication.id,
          medicationName: medication.medication_name,
          dosage: medication.preferred_dosage,
          frequency: medication.frequency,
          supplyDays: medication.supply_days,
          status: medication.status
        })
        // Medication selection functionality removed - medications tab no longer exists
      } else {
        console.warn('⚠️ MEDICATION NOT FOUND in patient preferences:', {
          searchingForId: initialMedicationId,
          availablePreferences: patient.medicationPreferences.map(m => ({ id: m.id, name: m.medication_name }))
        })
      }
    }
  }, [initialMedicationId, patient?.medicationPreferences])

  // Fetch shipping address when patient changes
  React.useEffect(() => {
    const fetchShippingAddress = async () => {
      if (!patient?.profile_id || !open) {
        setShippingAddress(null)
        return
      }

      setLoadingAddress(true)
      try {
        console.log('🏠 Fetching shipping address for patient profile_id:', patient.profile_id)
        const { data, error } = await authService.getPatientAddresses(patient.profile_id)

        if (error) {
          console.error('Error fetching patient addresses:', error)
          setShippingAddress(null)
          return
        }

        // Find the primary shipping address
        const primaryShipping = data?.find(
          (addr: any) => addr.address_type === 'shipping' && addr.is_primary
        )

        // Fallback to any shipping address if no primary
        const anyShipping = data?.find((addr: any) => addr.address_type === 'shipping')

        const address = primaryShipping || anyShipping

        if (address) {
          console.log('✅ Found shipping address:', address)
          setShippingAddress({
            street_line_1: address.street_line_1,
            street_line_2: address.street_line_2,
            city: address.city,
            state: address.state,
            postal_code: address.postal_code,
            country: address.country || 'US'
          })
        } else {
          console.log('ℹ️ No shipping address found for patient')
          setShippingAddress(null)
        }
      } catch (error) {
        console.error('Exception fetching shipping address:', error)
        setShippingAddress(null)
      } finally {
        setLoadingAddress(false)
      }
    }

    fetchShippingAddress()
  }, [patient?.profile_id, open])

  const getStartDate = (medication: PatientMedicationPreference) => {
    if (medication.status === 'approved') {
      return 'Started'
    }
    if (medication.status === 'pending' || medication.status === 'denied') {
      return 'Not started'
    }
    return 'Not started'
  }

  // Helper function to format time to 12-hour PST format
  const formatTimeTo12Hour = (timeString: string, dateString: string) => {
    try {
      const [year, month, day] = dateString.split('-').map(Number)
      
      // If time is already in AM/PM format, return as is
      if (timeString && /\d{1,2}:\d{2}\s*(AM|PM)/i.test(timeString)) {
        return timeString
      }
      
      // Parse 24-hour format like "14:30:00" or "14:30"
      if (timeString && /\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
        const [hours, minutes] = timeString.split(':').map(Number)
        const date = new Date(year, month - 1, day, hours, minutes, 0, 0)
        
        // Format to PST AM/PM
        return date.toLocaleTimeString('en-US', {
          timeZone: 'America/Los_Angeles',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      }
      
      return timeString
    } catch {
      return timeString
    }
  }

  // Helper function to categorize visits
  const categorizeVisits = (visits: PatientVisit[] = []) => {
    const now = new Date() // Current date and time
    
    console.log('🔍 CATEGORIZING VISITS:', {
      totalVisits: visits.length,
      currentTime: now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
      visits: visits.map(v => ({
        id: v.id,
        date: v.appointment_date,
        time: v.start_time,
        status: v.status,
        type: v.appointment_type
      }))
    })
    
    const upcoming = visits.filter(visit => {
      // Parse the appointment date components 
      const [year, month, day] = visit.appointment_date.split('-').map(Number)
      
      // Parse the start time (assuming format like "10:00 AM")
      const timeString = visit.start_time
      let appointmentDateTime: Date
      
      if (timeString) {
        // First try to parse AM/PM format like "10:00 AM" or "2:30 PM"
        const ampmParts = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
        if (ampmParts) {
          let hours = parseInt(ampmParts[1])
          const minutes = parseInt(ampmParts[2])
          const ampm = ampmParts[3].toUpperCase()
          
          // Convert to 24-hour format
          if (ampm === 'PM' && hours !== 12) {
            hours += 12
          } else if (ampm === 'AM' && hours === 12) {
            hours = 0
          }
          
          appointmentDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0)
        } else {
          // Try to parse 24-hour format like "14:30:00" or "14:30"
          const time24Parts = timeString.match(/(\d{1,2}):(\d{2})(:(\d{2}))?/)
          if (time24Parts) {
            const hours = parseInt(time24Parts[1])
            const minutes = parseInt(time24Parts[2])
            const seconds = time24Parts[4] ? parseInt(time24Parts[4]) : 0
            
            appointmentDateTime = new Date(year, month - 1, day, hours, minutes, seconds, 0)
          } else {
            // Fallback to date only if time can't be parsed
            appointmentDateTime = new Date(year, month - 1, day, 0, 0, 0, 0)
          }
        }
      } else {
        // No time provided, use date only
        appointmentDateTime = new Date(year, month - 1, day, 0, 0, 0, 0)
      }
      
      // Add 30-minute buffer after appointment time
      const appointmentEndTime = new Date(appointmentDateTime)
      appointmentEndTime.setMinutes(appointmentEndTime.getMinutes() + 30)
      
      // A visit is upcoming if:
      // 1. Appointment end time (+ 30 min buffer) is still in the future, AND
      // 2. Status is not completed, cancelled, or no_show
      const isUpcoming = appointmentEndTime > now && 
        !['completed', 'cancelled', 'no_show'].includes(visit.status)
      
      console.log('🔍 VISIT CATEGORIZATION:', {
        visitId: visit.id,
        date: visit.appointment_date,
        time: visit.start_time,
        appointmentDateTime: appointmentDateTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
        appointmentEndTime: appointmentEndTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
        currentTime: now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
        status: visit.status,
        timeComparison: appointmentEndTime > now ? 'future' : 'past',
        statusCheck: !['completed', 'cancelled', 'no_show'].includes(visit.status) ? 'active' : 'finished',
        result: isUpcoming ? 'UPCOMING' : 'not upcoming'
      })
      
      return isUpcoming
    })
    
    const previous = visits.filter(visit => {
      // Parse the appointment date components (same logic as above)
      const [year, month, day] = visit.appointment_date.split('-').map(Number)
      let appointmentDateTime: Date
      
      if (visit.start_time) {
        // First try to parse AM/PM format like "10:00 AM" or "2:30 PM"
        const ampmParts = visit.start_time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
        if (ampmParts) {
          let hours = parseInt(ampmParts[1])
          const minutes = parseInt(ampmParts[2])
          const ampm = ampmParts[3].toUpperCase()
          
          if (ampm === 'PM' && hours !== 12) {
            hours += 12
          } else if (ampm === 'AM' && hours === 12) {
            hours = 0
          }
          
          appointmentDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0)
        } else {
          // Try to parse 24-hour format like "14:30:00" or "14:30"
          const time24Parts = visit.start_time.match(/(\d{1,2}):(\d{2})(:(\d{2}))?/)
          if (time24Parts) {
            const hours = parseInt(time24Parts[1])
            const minutes = parseInt(time24Parts[2])
            const seconds = time24Parts[4] ? parseInt(time24Parts[4]) : 0
            
            appointmentDateTime = new Date(year, month - 1, day, hours, minutes, seconds, 0)
          } else {
            // Fallback to date only if time can't be parsed
            appointmentDateTime = new Date(year, month - 1, day, 0, 0, 0, 0)
          }
        }
      } else {
        // No time provided, use date only
        appointmentDateTime = new Date(year, month - 1, day, 0, 0, 0, 0)
      }
      
      const appointmentEndTime = new Date(appointmentDateTime)
      appointmentEndTime.setMinutes(appointmentEndTime.getMinutes() + 30)
      
      // A visit is previous if:
      // 1. Appointment end time (+ 30 min buffer) has passed, OR
      // 2. Status indicates it's finished (regardless of time)
      const isPrevious = appointmentEndTime <= now || 
        ['completed', 'cancelled', 'no_show'].includes(visit.status)
      
      return isPrevious
    })
    
    console.log('🔍 FINAL CATEGORIZATION RESULT:', {
      upcoming: upcoming.length,
      previous: previous.length,
      upcomingIds: upcoming.map(v => v.id),
      previousIds: previous.map(v => v.id)
    })
    
    return { upcoming, previous }
  }


  const handleBackToVisits = () => {
    setSelectedVisit(null)
    setClinicalNoteData(null)
    setExistingClinicalNote(null)
    setPendingMedicationChanges(null) // Clear any unsaved medication changes
    setNewAddendum('')
    setIsAddingAddendum(false)
    setActiveTab("Visits")
  }

  const handleVisitSelect = async (visit: PatientVisit) => {
    setSelectedVisit(visit)
    setExistingClinicalNote(null)
    
    // Always try to fetch existing clinical note and addendums for any visit
    // This ensures that when clicking from provider dashboard, we can see clinical notes
    console.log('🔍 PROVIDER DIALOG: Loading clinical note for visit:', visit.id)
    setLoadingClinicalNote(true)
    try {
      // Use services to fetch existing note and addendums
      
      // Fetch existing clinical note
      console.log('🔍 PROVIDER DIALOG: Fetching clinical note for appointment ID:', visit.id)
      console.log('🔍 PROVIDER DIALOG: Visit object details:', {
        id: visit.id,
        appointment_date: visit.appointment_date,
        start_time: visit.start_time,
        status: visit.status,
        prescription_filled: visit.prescription_filled
      })
      
      const existingNote = await ClinicalNotesService.getClinicalNoteByAppointmentId(visit.id)
      console.log('🔍 PROVIDER DIALOG: Clinical note query result:', existingNote)
      
      if (existingNote) {
        console.log('✅ Found existing clinical note:', existingNote)
        setExistingClinicalNote(existingNote)
        
        // Also fetch medication adjustments for this clinical note
        try {
          console.log('🔍 PROVIDER DIALOG: Fetching medication adjustments for clinical note ID:', existingNote.id)
          const medicationInteractions = await ClinicalNotesService.getVisitInteractionsByClinicalNoteId(existingNote.id)
          console.log('💊 Found medication adjustments for clinical note:', medicationInteractions)
          
          // Store medication adjustments in the clinical note for display
          setExistingClinicalNote({
            ...existingNote,
            medicationAdjustments: medicationInteractions
          })
        } catch (medError) {
          console.warn('⚠️ Error fetching medication adjustments:', medError)
        }
      } else {
        console.log('ℹ️ No existing clinical note found for appointment ID:', visit.id)
        console.log('🔍 PROVIDER DIALOG: This might mean:')
        console.log('   - No clinical note exists for this appointment')
        console.log('   - Visit ID format mismatch between visits and clinical_notes tables')
        console.log('   - Database permissions issue')
      }
      
      // Fetch existing addendums
      const addendums = await AddendumService.getAddendumsByVisitId(visit.id)
      console.log('📝 Found addendums for visit:', addendums)
      
      // Update the visit object with addendums
      const updatedVisit = { 
        ...visit, 
        addendums: addendums.map(addendum => ({
          id: addendum.id,
          visit_id: addendum.visit_id,
          provider_id: addendum.provider_id,
          content: addendum.content,
          created_at: addendum.created_at
        }))
      }
      setSelectedVisit(updatedVisit)
      
      // Fetch visit medication adjustments
      await fetchVisitMedicationAdjustments(visit.id)
      
    } catch (error) {
      console.warn('⚠️ Error fetching existing clinical note or addendums:', error)
    } finally {
      setLoadingClinicalNote(false)
    }
    
    // Initialize clinical note data for this visit (for upcoming visits or as fallback)
    setClinicalNoteData({
      appointmentId: visit.id,
      patientId: patient.id,
      providerId: providerId || '', // Use the provider ID passed from parent component
      allergies: [],
      previousMedications: [],
      currentMedications: [],
      clinicalNote: '',
      internalNote: '',
      visitSummary: ''
    })
  }


  // Load health metrics data for the patient using direct fetch API
  const loadHealthMetrics = React.useCallback(async (metricType: string = 'weight') => {
    if (!patient?.profile_id) return
    
    console.log('🔍 PROVIDER DIALOG: Loading health metrics for patient:', patient.profile_id, 'metric type:', metricType)
    
    try {
      // Using direct Supabase query instead of API endpoint
      
      // First get the patient's internal ID from profile_id using direct Supabase query
      console.log('🔍 PROVIDER DIALOG: Using direct Supabase query to get patient ID...')
      
      
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()
      
      if (patientError || !patientData) {
        console.error('❌ PROVIDER DIALOG: Failed to get patient ID:', patientError)
        setHealthMetricsData([])
        return
      }
      
      if (!patientData) {
        console.log('🔍 PROVIDER DIALOG: Patient record not found for profile_id:', patient.profile_id)
        setHealthMetricsData([])
        return
      }
      
      const patientId = patientData.id
      console.log('🔍 PROVIDER DIALOG: Found patient database ID:', patientId)
      
      // Now fetch health metrics using direct API call
      const startDate = new Date()
      startDate.setFullYear(startDate.getFullYear() - 1) // 1 year ago
      const endDate = new Date()
      
      // Map frontend metric types to database metric types
      const dbMetricType = metricType === 'heart-rate' ? 'heart_rate' : metricType
      
      const apiUrl = `/api/patients/health-metrics?patient_id=${patientId}&metric_type=${dbMetricType}&limit=1000&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`
      console.log('🔍 PROVIDER DIALOG: Fetching health metrics with URL:', apiUrl)
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      console.log('🚨 PROVIDER DIALOG: Health metrics fetch response status:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ PROVIDER DIALOG: Health metrics fetch error:', errorText)
        setHealthMetricsData([])
        return
      }
      
      const metricsResult = await response.json()
      const rawMetrics = metricsResult.data || []
      console.log('✅ PROVIDER DIALOG: Health metrics fetch success. Raw data count:', rawMetrics?.length)
      console.log('✅ PROVIDER DIALOG: Sample raw data:', rawMetrics?.slice(0, 3))
      
      // Transform to expected format
      const transformedMetrics = (rawMetrics || []).map(metric => ({
        date: metric.recorded_at.split('T')[0], // Convert to YYYY-MM-DD format
        value: metric.value,
        unit: metric.unit
      }))
      
      console.log('🔍 PROVIDER DIALOG: Transformed metrics count:', transformedMetrics.length)
      console.log('🔍 PROVIDER DIALOG: Sample transformed data:', transformedMetrics.slice(0, 3))
      setHealthMetricsData(transformedMetrics)
      
    } catch (error) {
      console.error('❌ PROVIDER DIALOG: Error loading health metrics:', error)
      setHealthMetricsData([])
    }
  }, [patient?.profile_id])

  // Load medication tracking data for the patient using medication tracking service (with proper enrichment)
  const loadMedicationTracking = React.useCallback(async () => {
    if (!patient?.profile_id) return
    
    console.log('🔍 PROVIDER DIALOG: Loading medication tracking for patient:', patient.profile_id)
    
    try {
      // Use the medication tracking service that properly enriches data with medication names
      
      console.log('🔍 PROVIDER DIALOG: Using medication tracking service for proper data enrichment...')
      
      // Using direct Supabase query instead of API endpoint
      
      // First get the patient's internal ID from profile_id using direct Supabase query
      
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()
      
      if (patientError || !patientData) {
        console.error('❌ PROVIDER DIALOG: Failed to get patient ID for medication tracking:', patientError)
        setMedicationTrackingData([])
        return
      }
      
      if (!patientData) {
        console.log('🔍 PROVIDER DIALOG: Patient record not found for medication tracking')
        setMedicationTrackingData([])
        return
      }
      
      const patientId = patientData.id
      console.log('🔍 PROVIDER DIALOG: Found patient database ID for medication tracking:', patientId)
      
      // Now fetch medication tracking entries with proper enrichment including medication names
      const apiUrl = `/api/patients/medication-tracking?patient_id=${patientId}&limit=1000`
      console.log('🔍 PROVIDER DIALOG: Fetching medication tracking with URL:', apiUrl)
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      console.log('🚨 PROVIDER DIALOG: Medication tracking fetch response status:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ PROVIDER DIALOG: Medication tracking fetch error:', errorText)
        setMedicationTrackingData([])
        return
      }
      
      const trackingResult = await response.json()
      const rawEntries = trackingResult.data || []
      console.log('✅ PROVIDER DIALOG: Medication tracking fetch success. Entry count:', rawEntries?.length)
      console.log('✅ PROVIDER DIALOG: Sample raw medication tracking data:', rawEntries?.slice(0, 2))
      
      if (rawEntries && rawEntries.length > 0) {
        // The data is already enriched with medication preference and medication info from the API
        console.log('✅ PROVIDER DIALOG: Using enriched medication tracking entries from API')
        console.log('✅ PROVIDER DIALOG: Sample tracking data:', rawEntries.slice(0, 2))
        console.log('🔍 PROVIDER DIALOG: First entry medication name:', rawEntries[0]?.patient_medication_preferences?.medications?.name || 'NOT FOUND')
        setMedicationTrackingData(rawEntries)
      } else {
        console.log('🔍 PROVIDER DIALOG: No medication tracking data found')
        setMedicationTrackingData([])
      }
    } catch (error) {
      console.error('❌ PROVIDER DIALOG: Error loading medication tracking:', error)
      setMedicationTrackingData([])
    }
  }, [patient?.profile_id])

  // Load available medications for the medication selector
  const loadAvailableMedications = React.useCallback(async () => {
    console.log('🔍 PROVIDER DIALOG: Loading available medications for selector...')
    setLoadingAvailableMedications(true)
    
    try {
      // Import Supabase dynamically
      
      // Get all active medications with their dosages
      const { data: medicationsResult, error: medicationsError } = await supabase
        .from('medications')
        .select(`
          id,
          name,
          brand_name,
          description,
          category,
          Eligibility,
          medication_dosages(strength)
        `)
        .eq('active', true)
        .order('name')
      
      if (medicationsError) {
        console.error('❌ PROVIDER DIALOG: Error loading medications:', medicationsError)
        setAvailableMedications([])
        return
      }
      
      if (medicationsResult) {
        // Transform medications data to include full dosage information for proper ID mapping
        const transformedMedications = medicationsResult.map(medication => ({
          id: medication.id,
          name: medication.name,
          brand_name: medication.brand_name,
          category: medication.category,
          available_dosages: medication.medication_dosages?.map((dosage: any) => dosage.strength) || [],
          // CRITICAL FIX: Include full dosage data for medication_dosage_id lookup
          medication_dosages: medication.medication_dosages?.map((dosage: any) => ({
            id: dosage.id,
            strength: dosage.strength
          })) || []
        }))
        
        console.log('✅ PROVIDER DIALOG: Loaded available medications:', transformedMedications)
        setAvailableMedications(transformedMedications)
      } else {
        console.log('⚠️ PROVIDER DIALOG: No medications found')
        setAvailableMedications([])
      }
    } catch (error) {
      console.error('❌ PROVIDER DIALOG: Error loading available medications:', error)
      setAvailableMedications([])
    } finally {
      setLoadingAvailableMedications(false)
    }
  }, [])

  // Load tracking data when patient is available (regardless of initial tab)
  React.useEffect(() => {
    if (patient?.profile_id) {
      console.log('🔍 PROVIDER DIALOG: Patient available, pre-loading tracking data for consistent experience')
      console.log('🔍 PROVIDER DIALOG: Current initialTab:', initialTab)
      console.log('🔍 PROVIDER DIALOG: Current activeTab:', activeTab)
      console.log('🔍 PROVIDER DIALOG: Current currentMetricType:', currentMetricType)
      loadHealthMetrics(currentMetricType)
      loadMedicationTracking()
      loadAvailableMedications()
    }
  }, [patient?.profile_id, loadHealthMetrics, loadMedicationTracking, loadAvailableMedications, currentMetricType])

  // Load metrics when metric type changes and tracking tab is active
  React.useEffect(() => {
    if (activeTab === 'Tracking' && patient?.profile_id) {
      console.log('🔍 PROVIDER DIALOG: Tracking tab active, loading current metric type:', currentMetricType)
      loadHealthMetrics(currentMetricType)
    }
  }, [activeTab, currentMetricType, loadHealthMetrics])


  // Handle metric type change for tracking chart
  const handleMetricChange = async (metricType: string) => {
    setCurrentMetricType(metricType)
    await loadHealthMetrics(metricType)
  }

  // Handle refresh for tracking chart
  const handleRefreshMetrics = () => {
    loadHealthMetrics(currentMetricType)
    loadMedicationTracking()
  }

  // Handle medication selection change for tracking chart
  const handleMedicationSelectionChange = (selectedIds: string[]) => {
    console.log('🔍 PROVIDER DIALOG: Medication selection changed:', selectedIds)
    setSelectedMedicationsForTracking(selectedIds)
  }

  const { upcoming: upcomingVisits, previous: previousVisits } = React.useMemo(() => {
    return categorizeVisits(patient?.visits || [])
  }, [patient?.visits])

  const handleSaveClinicalNote = async (): Promise<void> => {
    if (!selectedVisit || !clinicalNoteData) return
    
    // Prevent double execution during save process
    if (isSaving) {
      console.log('🛑 handleSaveClinicalNote blocked - already saving')
      return
    }
    
    // Validate that we have a provider ID
    if (!providerId) {
      console.error('Provider ID is required to save clinical notes.')
      return
    }

    setIsSaving(true)
    setIsHandlingClinicalNoteSave(true)  // Flag to disable individual toasts
    
    try {
      // Import services dynamically to avoid circular dependencies
      // Using ClinicalNotesService and supabase
      
      // Convert provider profile_id to database provider ID using direct Supabase query
      console.log('🔍 PROVIDER DIALOG: Using direct Supabase query to lookup provider for clinical note save...')
      
      const { data: providerData, error: providerError } = await supabase
        .from('providers')
        .select('*')
        .eq('profile_id', providerId)
        .single()

      if (providerError || !providerData) {
        console.error('❌ Error looking up provider:', providerError)
        throw new Error(`Failed to lookup provider: ${providerError?.message || 'Provider not found'}`)
      }

      const providerRecord = providerData
      console.log('✅ Found provider database ID:', providerRecord.id, 'for profile_id:', providerId)


      // Convert patient profile_id to database patient ID using direct Supabase query
      console.log('🔍 PROVIDER DIALOG: Using direct Supabase query to lookup patient for clinical note save...')
      
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient?.profile_id)
        .single()

      if (patientError || !patientData) {
        console.error('❌ Error looking up patient:', patientError)
        throw new Error(`Failed to lookup patient: ${patientError?.message || 'Patient not found'}`)
      }

      const patientRecord = patientData
      console.log('✅ Found patient database ID:', patientRecord.id, 'for profile_id:', patient?.profile_id)
      
      
      // Debug clinical note data before saving
      console.log('🔍 UI: Preparing to save clinical note...')
      console.log('🔍 UI: selectedVisit.id (appointmentId):', selectedVisit.id)
      console.log('🔍 UI: patientRecord.id:', patientRecord.id)
      console.log('🔍 UI: providerRecord.id:', providerRecord.id)
      console.log('🔍 UI: clinicalNoteData:', JSON.stringify(clinicalNoteData, null, 2))
      
      const clinicalNotePayload = {
        appointment_id: clinicalNoteData.appointmentId,
        patient_id: patientRecord.id, // Use the database patient ID
        provider_id: providerRecord.id, // Use the database provider ID
        allergies: clinicalNoteData.allergies,
        previous_medications: clinicalNoteData.previousMedications,
        current_medications: clinicalNoteData.currentMedications,
        clinical_note: clinicalNoteData.clinicalNote,
        internal_note: clinicalNoteData.internalNote,
        visit_summary: clinicalNoteData.visitSummary
      }
      
      console.log('🔍 UI: Final payload to service:', JSON.stringify(clinicalNotePayload, null, 2))
      
      // Save the clinical note - transform camelCase to snake_case
      const savedNote = await ClinicalNotesService.upsertClinicalNote(selectedVisit.id, clinicalNotePayload)
      
      console.log('Clinical note saved:', savedNote)
      
      // If there are pending medication changes, save them
      if (pendingMedicationChanges && onMedicationUpdate) {

        try {
          // Find the original medication state BEFORE applying changes
          const originalMed = patient?.medicationPreferences?.find(
            m => m.id === pendingMedicationChanges.id
          )

          console.log('🔍 PROVIDER DIALOG DEBUG: pendingMedicationChanges before save:', JSON.stringify(pendingMedicationChanges, null, 2))
          console.log('🔍 PROVIDER DIALOG DEBUG: other_dosage value specifically:', pendingMedicationChanges.other_dosage)
          
          await medicationUpdateWithToastControl(pendingMedicationChanges.id, {
            status: pendingMedicationChanges.status,
            dosage: pendingMedicationChanges.preferred_dosage,
            frequency: pendingMedicationChanges.frequency,
            providerNotes: pendingMedicationChanges.providerNotes,
            faxed: pendingMedicationChanges.status === 'approved',
            supplyDays: pendingMedicationChanges.supply_days,
            otherDosage: pendingMedicationChanges.other_dosage,
            medicationId: pendingMedicationChanges.medication_id // Pass the new medication_id when medication is changed
          })

          console.log('Pending medication changes saved as part of clinical note')

          // Record the medication adjustment in visit_medication_adjustments table
          // This preserves historical data about what medications looked like at each visit
          try {
            // Get the medication ID - use the new one if medication changed, otherwise the original
            // Note: originalMed.id is the PREFERENCE id, not medication id
            // Use originalMed.medications?.id as fallback (from the joined medications table)
            const medicationId = pendingMedicationChanges.medication_id || originalMed?.medication_id || originalMed?.medications?.id

            console.log('📝 Medication ID lookup for adjustment record:', {
              'pendingMedicationChanges.medication_id': pendingMedicationChanges.medication_id,
              'originalMed?.medication_id': originalMed?.medication_id,
              'originalMed?.medications?.id': originalMed?.medications?.id,
              'final medicationId': medicationId
            })

            // Build provider notes that includes other_dosage tracking
            const notesParts = []
            if (pendingMedicationChanges.providerNotes) {
              notesParts.push(pendingMedicationChanges.providerNotes)
            }
            
            // Add other_dosage tracking if it changed
            const previousOtherDosage = originalMed?.other_dosage || null
            const newOtherDosage = pendingMedicationChanges.other_dosage || null
            if (previousOtherDosage !== newOtherDosage) {
              notesParts.push(`Other dosage changed from "${previousOtherDosage || 'none'}" to "${newOtherDosage || 'none'}"`)
            }

            const adjustmentRecord = {
              clinical_note_id: savedNote.id,
              appointment_id: selectedVisit.id,
              preference_id: pendingMedicationChanges.id,
              medication_id: medicationId, // Store the actual medication ID for historical lookup
              // Previous values (before this visit's changes)
              previous_dosage: originalMed?.preferred_dosage || null,
              previous_frequency: originalMed?.frequency || null,
              previous_status: originalMed?.status || null,
              previous_provider_notes: originalMed?.notes || originalMed?.providerNotes || null,
              // New values (after this visit's changes)
              new_dosage: pendingMedicationChanges.preferred_dosage || null,
              new_frequency: pendingMedicationChanges.frequency || null,
              new_status: pendingMedicationChanges.status || null,
              new_provider_notes: notesParts.length > 0 ? notesParts.join('; ') : null,
              new_supply_days: pendingMedicationChanges.supply_days || null,
              // Metadata
              performed_by: providerRecord.id,
              created_at: new Date().toISOString()
            }

            console.log('📝 Recording medication adjustment to visit_medication_adjustments:', adjustmentRecord)

            // Use the enhanced ClinicalNotesService function instead of direct insert
            await ClinicalNotesService.createVisitMedicationAdjustment(adjustmentRecord)
            console.log('✅ Medication adjustment recorded successfully via ClinicalNotesService')
          } catch (adjError) {
            console.error('❌ Exception recording medication adjustment:', adjError)
            // Don't throw - this is supplementary data
          }

          // Clear pending changes after successful save
          setPendingMedicationChanges(null)
        } catch (medError) {
          console.error('Error updating medication preference:', medError)
          // Don't throw here, continue with success flow
        }
      }

      // Update the visit status to completed in local state
      if (selectedVisit) {
        selectedVisit.status = 'completed'
      }
      
      // CRITICAL FIX: Refresh patient visits from database after clinical note completion
      // This ensures the completed visit appears in the "Previous Visits" tab
      try {
        console.log('🔄 CLINICAL NOTE SAVE: Refreshing patient visits after completion...')
        
        // Fetch updated visits from database using the same logic as parent component
        const appointmentsResult = await authService.getPatientAppointments(patient?.profile_id, false)
        
        if (appointmentsResult.data && patient) {
          // Convert appointments to PatientVisit format (same logic as parent component)
          const updatedVisits = appointmentsResult.data
            .map((appt: any) => ({
              id: appt.appointment_id || appt.id,
              appointment_date: appt.appointment_date,
              start_time: appt.start_time,
              appointment_type: appt.appointment_type || 'consultation',
              treatment_type: appt.treatment_type,
              status: appt.status, // This will include the newly updated 'completed' status
              provider_notes: appt.provider_notes,
              patient_notes: appt.patient_notes
            }))
            .sort((a: any, b: any) => {
              // Sort by date descending, then by time descending (most recent first)
              const dateCompare = new Date(b.appointment_date).getTime() - new Date(a.appointment_date).getTime()
              if (dateCompare === 0) {
                return b.start_time.localeCompare(a.start_time)
              }
              return dateCompare
            })
          
          console.log('✅ CLINICAL NOTE SAVE: Updated patient visits with refreshed data:', updatedVisits.length)
          console.log('✅ CLINICAL NOTE SAVE: Completed visit should now be in list:', updatedVisits.find(v => v.id === selectedVisit.id))
          
          // Update the patient object with refreshed visits
          // This will cause the useMemo for categorizeVisits to recalculate
          patient.visits = updatedVisits
        }
      } catch (refreshError) {
        console.warn('⚠️ CLINICAL NOTE SAVE: Failed to refresh patient visits:', refreshError)
        // Don't fail the overall save operation if refresh fails
      }
      
      // Navigate back to visits and switch to previous tab
      setVisitsTab('previous')
      handleBackToVisits()
    } catch (error) {
      console.error('Error saving clinical note:', error)
    } finally {
      setIsSaving(false)
      setIsHandlingClinicalNoteSave(false)  // Reset flag
    }
  }

  const handleAddendumSubmit = async (): Promise<void> => {
    if (!selectedVisit || !newAddendum.trim() || !providerId) return

    setIsAddingAddendum(true)

    try {
      console.log('🚨 ADDENDUM FIX 2026-01-11: Adding addendum for visit:', selectedVisit.id, 'Content:', newAddendum)
      console.log('🚨 ADDENDUM FIX 2026-01-11: Provider profile_id:', providerId)

      // Convert provider profile_id to database provider ID using service client to bypass RLS
      const { createClient } = await import('@supabase/supabase-js')
      const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('@joinomu/shared')
      
      const serviceClient = createClient(
        getEnvironmentAwareSupabaseUrl(),
        getEnvironmentAwareSupabaseServiceKey(),
        {
          auth: {
            autoRefreshToken: false,
            persistSession: false,
            detectSessionInUrl: false
          }
        }
      )

      const { data: providerData, error: providerError } = await serviceClient
        .from('providers')
        .select('id')
        .eq('profile_id', providerId)
        .single()

      if (providerError || !providerData) {
        console.error('❌ ADDENDUM FIX 2026-01-11: Error looking up provider:', providerError)
        throw new Error(`Failed to lookup provider: ${providerError?.message || 'Provider not found'}`)
      }

      console.log('✅ ADDENDUM FIX 2026-01-11: Found provider database ID:', providerData.id, 'for profile_id:', providerId)

      // Save the addendum with correct provider database ID
      const savedAddendum = await AddendumService.createAddendum({
        visit_id: selectedVisit.id,
        provider_id: providerData.id,
        content: newAddendum.trim()
      })

      console.log('Addendum saved:', savedAddendum)

      // Update the selected visit with the new addendum
      const updatedAddendums = [
        ...(selectedVisit.addendums || []),
        {
          id: savedAddendum.id,
          visit_id: savedAddendum.visit_id,
          provider_id: savedAddendum.provider_id,
          content: savedAddendum.content,
          created_at: savedAddendum.created_at
        }
      ]

      setSelectedVisit(prev => prev ? { ...prev, addendums: updatedAddendums } : null)
      setNewAddendum('')
      
      // Show success toast
      showToast({
        title: "Addendum Saved",
        description: "Visit addendum has been successfully added",
        variant: 'success'
      })
    } catch (error) {
      console.error('Error saving addendum:', error)
      
      // Show error toast to user
      showToast({
        title: "Failed to Save Addendum",
        description: error instanceof Error 
          ? `Error: ${error.message}` 
          : "An unexpected error occurred while saving the addendum. Please try again.",
        variant: 'error'
      })
    } finally {
      setIsAddingAddendum(false)
    }
  }


  // Handle null patient case properly to fix React error #310
  if (!patient) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={`overflow-hidden p-0 bg-card ${
          isFullscreen
            ? "!fixed !inset-0 !w-screen !h-screen !max-w-none !max-h-none !rounded-none !border-0 !left-0 !top-0 !transform-none"
            : "w-[calc(100vw-8px)] h-[calc(100dvh-8px)] md:w-auto md:h-auto md:max-h-[500px] md:max-w-[700px] lg:max-w-[800px]"
        }`}>
          <DialogTitle className="sr-only">Patient Information - Provider View</DialogTitle>
          <DialogDescription className="sr-only">
            Patient information is loading...
          </DialogDescription>
          <div className="flex items-center justify-center h-48">
            <div className="text-muted-foreground">Loading patient information...</div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`overflow-hidden p-0 bg-card ${
        isFullscreen
          ? "!fixed !inset-0 !w-screen !h-screen !max-w-none !max-h-none !rounded-none !border-0 !left-0 !top-0 !transform-none"
          : "w-[calc(100vw-8px)] h-[calc(100dvh-8px)] md:w-auto md:h-auto md:max-h-[500px] md:max-w-[700px] lg:max-w-[800px]"
      }`}>
        <DialogTitle className="sr-only">Patient Information - Provider View</DialogTitle>
        <DialogDescription className="sr-only">
          View and manage patient information here from provider perspective.
        </DialogDescription>
        <SidebarProvider className="items-start">
          <Sidebar collapsible="none" className="hidden md:flex w-64 border-r border-sidebar-border dark:border-transparent">
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {patientNavData.nav.map((item) => (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton
                          asChild
                          isActive={activeTab === item.name}
                          onClick={() => setActiveTab(item.name)}
                        >
                          <button>
                            <item.icon />
                            <span>{item.name}</span>
                          </button>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
          <main className="flex flex-1 flex-col overflow-hidden bg-card h-full">
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
              <div className="flex items-center gap-2 px-4 w-full">
                {/* Mobile Navigation Dropdown - only show on small screens */}
                <div className="flex md:hidden mr-2">
                  <Popover open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-auto justify-between border border-border hover:bg-muted px-3 h-8"
                      >
                        <span className="truncate">{activeTab}</span>
                        <ChevronDown className="h-4 w-4 ml-1 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="space-y-1">
                        {patientNavData.nav.map((item) => (
                          <Button
                            key={item.name}
                            variant={item.name === activeTab ? "secondary" : "ghost"}
                            size="sm"
                            className="w-full justify-start"
                            onClick={() => {
                              setActiveTab(item.name)
                              setSelectedVisit(null)
                              setPendingMedicationChanges(null)
                              setIsMobileMenuOpen(false)
                            }}
                          >
                            <item.icon className="mr-2 h-4 w-4" />
                            {item.name}
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hidden md:block"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? (
                    <Minimize className="h-4 w-4" />
                  ) : (
                    <Maximize className="h-4 w-4" />
                  )}
                </Button>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem className="hidden md:block">
                      <BreadcrumbLink 
                        href="#" 
                        onClick={() => {
                          setActiveTab("Patient Information")
                          setPendingMedicationChanges(null) // Clear any unsaved medication changes
                        }}
                      >
                        Patient Information
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator className="hidden md:block" />
                    {selectedVisit && activeTab === "Visits" ? (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#" onClick={handleBackToVisits}>
                            Visits
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbPage>Clinical Note</BreadcrumbPage>
                        </BreadcrumbItem>
                      </>
                    ) : (
                      <BreadcrumbItem>
                        <BreadcrumbPage>{activeTab}</BreadcrumbPage>
                      </BreadcrumbItem>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
            </header>
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 pt-0">
              {activeTab === "Patient Information" && (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-xl p-4">
                    <h3 className="font-semibold mb-2">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Name:</span>
                        <p>{patient.name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Email:</span>
                        <p>{patient.email}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Date of Birth:</span>
                        <p>{patient.dateOfBirth || 'Not provided'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Patient ID:</span>
                        <p>{patient.patientId || patient.id}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Phone:</span>
                        <p>{patient.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Gender:</span>
                        <p>{patient.gender || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/50 rounded-xl p-4">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Shipping Address
                    </h3>
                    {loadingAddress ? (
                      <p className="text-sm text-muted-foreground">Loading address...</p>
                    ) : shippingAddress ? (
                      <div className="text-sm">
                        <p>{shippingAddress.street_line_1}</p>
                        {shippingAddress.street_line_2 && (
                          <p>{shippingAddress.street_line_2}</p>
                        )}
                        <p>
                          {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                        </p>
                        {shippingAddress.country && shippingAddress.country !== 'US' && (
                          <p>{shippingAddress.country}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No shipping address on file</p>
                    )}
                  </div>

                  <div className="bg-muted/50 rounded-xl p-4">
                    <h3 className="font-semibold mb-2">Treatment Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Treatment Type:</span>
                        <p>{patient.treatmentType || 'General Care'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Assigned Date:</span>
                        <p>{patient.assignedDate || 'Not specified'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Primary Provider:</span>
                        <p>{patient.isPrimary ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <p>Active</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {activeTab === "Visits" && !selectedVisit && (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-xl p-4">
                    <Tabs value={visitsTab} onValueChange={(value) => setVisitsTab(value as 'upcoming' | 'previous')} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                        <TabsTrigger value="previous">Previous</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="upcoming" className="mt-4">
                        {upcomingVisits.length === 0 ? (
                          <div>
                            <p className="text-sm text-muted-foreground">No upcoming appointments found for this patient.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {upcomingVisits.map((visit) => {
                              // Check if visit is in the past
                              const [year, month, day] = visit.appointment_date.split('-').map(Number)
                              const visitDate = new Date(year, month - 1, day) // month is 0-indexed
                              const today = new Date()
                              today.setHours(0, 0, 0, 0) // Set to start of day for accurate comparison
                              visitDate.setHours(0, 0, 0, 0)
                              
                              const isInPast = visitDate < today
                              
                              // Determine the actual status - if in the past and still "scheduled", show as "completed"
                              const actualStatus = (isInPast && visit.status === 'scheduled') ? 'completed' : visit.status
                              
                              return (
                                <VisitCard
                                  key={visit.id}
                                  patientName={patient.name}
                                  appointmentDate={visit.appointment_date}
                                  appointmentTime={formatTimeTo12Hour(visit.start_time, visit.appointment_date)}
                                  visitType={visit.appointment_type}
                                  status={actualStatus}
                                  treatmentType={visit.treatment_type}
                                  onClick={() => handleVisitSelect(visit)}
                                  className="w-full max-w-none cursor-pointer"
                                />
                              )
                            })}
                          </div>
                        )}
                      </TabsContent>
                      
                      <TabsContent value="previous" className="mt-4">
                        {previousVisits.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No previous appointments found for this patient.</p>
                        ) : (
                          <div className="space-y-4">
                            {previousVisits.map((visit) => {
                              const today = new Date()
                              today.setHours(0, 0, 0, 0)
                              
                              const visitDate = new Date(visit.appointment_date)
                              visitDate.setHours(0, 0, 0, 0)
                              
                              const isInPast = visitDate < today
                              
                              // Determine the actual status - if in the past and still "scheduled", show as "completed"
                              const actualStatus = (isInPast && visit.status === 'scheduled') ? 'completed' : visit.status
                              
                              return (
                                <VisitCard
                                  key={visit.id}
                                  patientName={patient.name}
                                  appointmentDate={visit.appointment_date}
                                  appointmentTime={formatTimeTo12Hour(visit.start_time, visit.appointment_date)}
                                  visitType={visit.appointment_type}
                                  status={actualStatus}
                                  treatmentType={visit.treatment_type}
                                  onClick={() => handleVisitSelect(visit)}
                                  className="w-full max-w-none cursor-pointer"
                                />
                              )
                            })}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              )}

              {activeTab === "Visits" && selectedVisit && clinicalNoteData && (
                <div className="space-y-4">
                  {/* Check if prescription is filled (read-only) - medication adjustment only available when prescription_filled = false */}
                  {selectedVisit.prescription_filled === true ? (
                    // Read-only view for previous visits
                    <div className="space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>Visit Details - {(() => {
                            // Parse date safely to avoid timezone issues
                            const [year, month, day] = selectedVisit.appointment_date.split('-').map(Number)
                            const date = new Date(year, month - 1, day) // month is 0-indexed
                            return date.toLocaleDateString()
                          })()}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Patient:</span>
                              <p>{patient.name}</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Appointment Time:</span>
                              <p>{selectedVisit.start_time}</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Visit Type:</span>
                              <p>{selectedVisit.appointment_type}</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Status:</span>
                              <p className="capitalize">{selectedVisit.status}</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Treatment Type:</span>
                              <p>{selectedVisit.treatment_type}</p>
                            </div>
                          </div>
                          
                          {selectedVisit.provider_notes && (
                            <div>
                              <Label className="text-sm font-medium">Provider Notes</Label>
                              <div className="mt-1 p-3 bg-muted rounded-md">
                                <p className="text-sm">{selectedVisit.provider_notes}</p>
                              </div>
                            </div>
                          )}
                          
                          {selectedVisit.patient_notes && (
                            <div>
                              <Label className="text-sm font-medium">Patient Notes</Label>
                              <div className="mt-1 p-3 bg-muted rounded-md">
                                <p className="text-sm">{selectedVisit.patient_notes}</p>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      {/* Visit Medication Adjustments - HIDDEN: Contains incorrect info, will be fixed in staging */}
                      {false && visitMedicationAdjustments.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle>Medication Adjustments During Visit</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {visitMedicationAdjustments.map((adjustment) => (
                              <div key={adjustment.id} className="p-4 border rounded-md bg-blue-50/50">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-sm font-medium">{adjustment.medication_name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {new Date(adjustment.created_at).toLocaleString()}
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-muted-foreground font-medium">Previous:</span>
                                    <div className="ml-2">
                                      {adjustment.previous_status && (
                                        <div>Status: <span className="capitalize">{adjustment.previous_status}</span></div>
                                      )}
                                      {adjustment.previous_dosage && (
                                        <div>Dosage: {adjustment.previous_dosage}</div>
                                      )}
                                      {adjustment.previous_frequency && (
                                        <div>Frequency: {adjustment.previous_frequency}</div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <span className="text-muted-foreground font-medium">Updated to:</span>
                                    <div className="ml-2">
                                      {adjustment.new_status && (
                                        <div>Status: <span className="capitalize font-medium">{adjustment.new_status}</span></div>
                                      )}
                                      {adjustment.new_dosage && (
                                        <div>Dosage: <span className="font-medium">{adjustment.new_dosage}</span></div>
                                      )}
                                      {adjustment.new_frequency && (
                                        <div>Frequency: <span className="font-medium">{adjustment.new_frequency}</span></div>
                                      )}
                                      {adjustment.new_supply_days && (
                                        <div>Supply: <span className="font-medium">{adjustment.new_supply_days} days</span></div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {adjustment.adjustment_reason && (
                                  <div className="mt-2 text-sm">
                                    <span className="text-muted-foreground font-medium">Reason: </span>
                                    {adjustment.adjustment_reason}
                                  </div>
                                )}
                                
                                {adjustment.new_provider_notes && (
                                  <div className="mt-2 text-sm">
                                    <span className="text-muted-foreground font-medium">Provider Notes: </span>
                                    {adjustment.new_provider_notes}
                                  </div>
                                )}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Note: Historical medication data at time of visit not available -
                          visit_medication_adjustments table needs to be populated from clinical notes */}
                      
                      {/* Clinical Note Display */}
                      {loadingClinicalNote ? (
                        <Card>
                          <CardHeader>
                            <CardTitle>Clinical Note</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground">Loading clinical note...</p>
                          </CardContent>
                        </Card>
                      ) : existingClinicalNote ? (
                        <Card>
                          <CardHeader>
                            <CardTitle>Clinical Note</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {existingClinicalNote.allergies && Array.isArray(existingClinicalNote.allergies) && existingClinicalNote.allergies.length > 0 && (
                              <div>
                                <Label className="text-sm font-medium">Allergies</Label>
                                <div className="mt-1 p-3 bg-muted rounded-md">
                                  <p className="text-sm">{existingClinicalNote.allergies.join(', ')}</p>
                                </div>
                              </div>
                            )}
                            
                            {existingClinicalNote.previous_medications && Array.isArray(existingClinicalNote.previous_medications) && existingClinicalNote.previous_medications.length > 0 && (
                              <div>
                                <Label className="text-sm font-medium">Previous Medications</Label>
                                <div className="mt-1 p-3 bg-muted rounded-md">
                                  <p className="text-sm">{existingClinicalNote.previous_medications.join(', ')}</p>
                                </div>
                              </div>
                            )}
                            
                            {existingClinicalNote.current_medications && Array.isArray(existingClinicalNote.current_medications) && existingClinicalNote.current_medications.length > 0 && (
                              <div>
                                <Label className="text-sm font-medium">Current Medications</Label>
                                <div className="mt-1 p-3 bg-muted rounded-md">
                                  <p className="text-sm">{existingClinicalNote.current_medications.join(', ')}</p>
                                </div>
                              </div>
                            )}
                            
                            {existingClinicalNote.clinical_note && (
                              <div>
                                <Label className="text-sm font-medium">Clinical Note</Label>
                                <div className="mt-1 p-3 bg-muted rounded-md">
                                  <p className="text-sm">{existingClinicalNote.clinical_note}</p>
                                </div>
                              </div>
                            )}
                            
                            {existingClinicalNote.internal_note && (
                              <div>
                                <Label className="text-sm font-medium">Internal Note</Label>
                                <div className="mt-1 p-3 bg-muted rounded-md">
                                  <p className="text-sm">{existingClinicalNote.internal_note}</p>
                                </div>
                              </div>
                            )}
                            
                            {existingClinicalNote.visit_summary && (
                              <div>
                                <Label className="text-sm font-medium">Visit Summary</Label>
                                <div className="mt-1 p-3 bg-muted rounded-md">
                                  <p className="text-sm">{existingClinicalNote.visit_summary}</p>
                                </div>
                              </div>
                            )}
                            
                            {existingClinicalNote.medicationAdjustments && existingClinicalNote.medicationAdjustments.length > 0 && (
                              <div>
                                <Label className="text-sm font-medium">Medication Adjustments</Label>
                                <div className="mt-1 space-y-2">
                                  {existingClinicalNote.medicationAdjustments.map((adjustment: any, index: number) => (
                                    <div key={adjustment.id || index} className="p-3 bg-muted rounded-md">
                                      <div className="text-sm font-medium">{adjustment.medication_name || 'Medication Adjustment'}</div>
                                      {adjustment.previous_dosage && (
                                        <div className="text-xs text-muted-foreground">Previous: {adjustment.previous_dosage}</div>
                                      )}
                                      {adjustment.new_dosage && (
                                        <div className="text-xs text-muted-foreground">New: {adjustment.new_dosage}</div>
                                      )}
                                      {adjustment.notes && (
                                        <div className="text-xs mt-1">{adjustment.notes}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="text-xs text-muted-foreground">
                              Created: {new Date(existingClinicalNote.created_at).toLocaleString()}
                              {existingClinicalNote.updated_at !== existingClinicalNote.created_at && (
                                <span> • Updated: {new Date(existingClinicalNote.updated_at).toLocaleString()}</span>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card>
                          <CardHeader>
                            <CardTitle>Clinical Note</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground">No clinical note found for this visit.</p>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Addendums section */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Addendums</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Display existing addendums */}
                          {selectedVisit.addendums && selectedVisit.addendums.length > 0 ? (
                            <div className="space-y-3">
                              {selectedVisit.addendums
                                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                                .map((addendum) => (
                                <div key={addendum.id} className="p-3 bg-muted rounded-md">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(addendum.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-sm">{addendum.content}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No addendums for this visit.</p>
                          )}
                          
                          {/* Add new addendum */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Add Addendum</Label>
                            <Textarea
                              value={newAddendum}
                              onChange={(e) => setNewAddendum(e.target.value)}
                              placeholder="Enter addendum content..."
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <Button 
                                onClick={handleAddendumSubmit}
                                disabled={!newAddendum.trim() || isAddingAddendum}
                                size="sm"
                              >
                                {isAddingAddendum ? 'Adding...' : 'Add Addendum'}
                              </Button>
                              <Button 
                                variant="outline" 
                                onClick={handleBackToVisits}
                                size="sm"
                              >
                                Back to Visits
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    // Editable view for upcoming visits
                    <VisitClinicalNote
                      visit={selectedVisit}
                      patientName={patient.name}
                      patientProfileId={patient.profile_id}
                      providerProfileId={providerId}
                      medications={patient.medicationPreferences ? patient.medicationPreferences
                        .map(pref => ({
                          id: pref.id,
                          medication_id: (pref as any).medication_id || 'unknown', // Add medication_id field with fallback
                          medication_name: pref.medication_name,
                          preferred_dosage: pref.preferred_dosage,
                          frequency: pref.frequency,
                          status: pref.status,
                          providerNotes: pref.notes,
                          faxed: null, // Always start with unchecked fax checkbox for new visits
                          other_dosage: pref.other_dosage // Add other_dosage field
                        })) : []}
                      clinicalNote={clinicalNoteData}
                      onMedicationChange={(medicationId, medication) => {
                        console.log('Medication changed locally:', medicationId, medication)
                        // Store the medication changes locally, don't save to API yet
                        setPendingMedicationChanges(medication)
                      }}
                      onClinicalNoteChange={(note) => {
                        setClinicalNoteData(note)
                      }}
                      onMedicationFax={async (medicationId, shouldFax, patientProfileId, providerProfileId) => {
                        if (shouldFax && patientProfileId && providerProfileId) {
                          try {
                            setIsSaving(true)
                            // Import supabase for direct fax record creation
                                                  
                            // Find the medication from pending changes or patient preferences
                            const medication = pendingMedicationChanges || 
                              (patient.medicationPreferences && patient.medicationPreferences.find(m => m.id === medicationId))
                            
                            if (!medication) {
                              throw new Error('Medication not found')
                            }
                            
                            // Get the correct patient table ID and provider table ID using direct Supabase queries
                            console.log('🔍 Looking up patient with profile_id:', patientProfileId)
                            
                            const { data: patientRecord, error: patientError } = await supabase
                              .from('patients')
                              .select('*')
                              .eq('profile_id', patientProfileId)
                              .single()
                            
                            if (patientError || !patientRecord) {
                              throw new Error(`Failed to get patient table ID: ${patientError?.message || 'Patient not found'}`)
                            }
                            
                            console.log('🔍 Patient lookup result:', { patientRecord })
                            
                            console.log('🔍 Looking up provider with profile_id:', providerProfileId)
                            const { data: providerRecord, error: providerError } = await supabase
                              .from('providers')
                              .select('*')
                              .eq('profile_id', providerProfileId)
                              .single()
                            
                            if (providerError || !providerRecord) {
                              throw new Error(`Failed to get provider table ID: ${providerError?.message || 'Provider not found'}`)
                            }
                            
                            console.log('🔍 Provider lookup result:', { providerRecord })
                            
                            if (!patientRecord || !providerRecord) {
                              throw new Error('Failed to get patient or provider table IDs')
                            }
                            
                            // 🎯 FIX: Use the auth service method that properly creates approval AND fax records
                            // This ensures the database trigger has all the required data to create orders
                            console.log('🔄 Using auth service to create approval and fax for preference:', medicationId)
                            
                            const faxCreationResult = await authService.createMedicationApprovalAndFax(
                              medicationId,      // preference ID
                              providerProfileId, // provider profile ID  
                              patientProfileId   // patient profile ID
                            )
                            
                            console.log('🔍 Auth service fax creation result:', faxCreationResult)
                            
                            if (faxCreationResult.error) {
                              console.error('❌ Auth service fax creation failed:', faxCreationResult.error)
                              throw new Error(`Failed to create approval and fax: ${faxCreationResult.error.message || faxCreationResult.error}`)
                            }
                            
                            if (!faxCreationResult.data || !faxCreationResult.data.approvalId) {
                              throw new Error('Failed to get approval ID from auth service')
                            }
                            
                            console.log('✅ Successfully created approval and fax via auth service:', {
                              approvalId: faxCreationResult.data.approvalId,
                              faxId: faxCreationResult.data.faxId
                            })
                            
                            // ✅ Auth service already created both the approval and fax records
                            // This should have automatically triggered the database trigger to create the order
                            console.log('🎯 Order creation should have been triggered automatically by the fax trigger')
                            
                            // Update medication preference with faxed timestamp
                            // Note: Using wrapped version to prevent external toasts during clinical note save
                            await medicationUpdateWithToastControl(medicationId, {
                              status: 'approved',
                              faxed: true
                            })
                            
                            
                          } catch (error) {
                            console.error('Error faxing medication from clinical note:', error)
                            
                          } finally {
                            setIsSaving(false)
                          }
                        }
                      }}
                      availableMedications={availableMedications}
                      onSave={handleSaveClinicalNote}
                      isSaving={isSaving}
                      showProgressToasts={false}  // Parent component handles progress toasts
                    />
                  )}
                </div>
              )}

              {activeTab === "Tracking" && (
                <div className="space-y-4">
                  <TrackingChart
                    title="Patient Health Tracking"
                    description="View patient's health metrics and medication tracking data"
                    className="border-0 shadow-none bg-transparent"
                      selectedMetric={currentMetricType}
                      affirmation="Monitoring patient progress helps optimize treatment outcomes."
                      medications={[]} // Simplified for provider view - no medication overlay needed
                      selectedMedications={selectedMedicationsForTracking}
                      onMedicationSelectionChange={handleMedicationSelectionChange}
                      onAddMetrics={() => {}} // Disabled for providers
                      hasMetrics={healthMetricsData.length > 0}
                      metricsData={healthMetricsData}
                      patientId={patient.profile_id}
                      onRefresh={handleRefreshMetrics}
                      onMetricChange={handleMetricChange}
                      medicationTrackingEntries={medicationTrackingData}
                      showAddMetricsButton={false} // Hide button for providers
                    />
                </div>
              )}

              {activeTab === "History" && (
                <div className="space-y-4">
                  <PrescriptionHistory 
                    patientId={patient?.id || patient?.profile_id || ''} 
                    className="w-full"
                  />
                </div>
              )}
              {activeTab === "Eligibility" && (
                <EligibilitySection patient={patient} />
              )}
            </div>
          </main>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  )
}